/**
 * Decode a stored image blob into a canvas suitable for a Pixi texture.
 *
 * Unlike text cards, images do NOT go through the expensive html-to-image
 * pipeline — they are decoded straight to a bitmap. The on-disk file keeps its
 * full resolution; the GPU texture is capped on its long edge so a handful of
 * phone screenshots can't exhaust VRAM (a 4000×3000 image would otherwise eat
 * ~48 MB each). This mirrors the LOD philosophy used for text rasterization.
 */

/** Max texels on the long edge of an uploaded image texture. */
export const MAX_TEXTURE_DIM = 2048;

/**
 * Los bytes de una imagen como data URL.
 *
 * Es la única forma de meter una imagen del vault en un snapshot de tarjeta:
 * `html-to-image` serializa el nodo a un SVG y ahí adentro un `src` que apunte
 * a un archivo (o a un blob URL) no se carga; tiene que venir embebido.
 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

interface Decoded {
  width: number;
  height: number;
  draw(ctx: CanvasRenderingContext2D, w: number, h: number): void;
  release(): void;
}

async function decode(blob: Blob): Promise<Decoded> {
  // Fast path: createImageBitmap handles most raster formats off the main work.
  try {
    const bmp = await createImageBitmap(blob);
    return {
      width: bmp.width,
      height: bmp.height,
      draw: (ctx, w, h) => ctx.drawImage(bmp, 0, 0, w, h),
      release: () => bmp.close(),
    };
  } catch {
    // Fallback via <img> for blobs createImageBitmap rejects.
    const url = URL.createObjectURL(blob);
    const img = new Image();
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error("image decode failed"));
      img.src = url;
    });
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
      release: () => URL.revokeObjectURL(url),
    };
  }
}

/** Intrinsic pixel dimensions of an image blob (used to size the pasted card). */
export async function imageSize(blob: Blob): Promise<{ width: number; height: number }> {
  const d = await decode(blob);
  const { width, height } = d;
  d.release();
  return { width, height };
}

/**
 * Decode `blob` to a canvas, downscaling uniformly so the long edge is at most
 * `maxDim`. Returns the canvas the caller uploads to a Pixi texture.
 */
export async function decodeImageToCanvas(
  blob: Blob,
  maxDim = MAX_TEXTURE_DIM,
): Promise<HTMLCanvasElement> {
  const d = await decode(blob);
  try {
    const scale = Math.min(1, maxDim / Math.max(d.width, d.height));
    const w = Math.max(1, Math.round(d.width * scale));
    const h = Math.max(1, Math.round(d.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingQuality = "high";
    d.draw(ctx, w, h);
    return canvas;
  } finally {
    d.release();
  }
}
